// Протоколът за анулиране на софтуерна фактура (ЗДДС чл. 116) — POS печат през
// BE `POST /invoices/{id}/print-cancellation`, на принтера на фактурата.
//
// Анулира се (чл. 116 — документът е сгрешен и се заменя с нов) от три места:
// „Анулирай" в прегледа на фактурата, „Възстанови" в „Справка сметки" и
// „Възстановяване" в „Маркиране" (софтуерната фактура на сметката се анулира
// сама, след новото закриване се издава нова). „Сторнирай всичко" и анулиран
// депозит НЕ анулират — продажбата е развалена и BE издава кредитно известие
// (чл. 115, `useCreditNoteOffer`). След всяко `offer` пита „Отпечатай
// протокол?"; бутонът „Протокол (POS)" в прегледа на анулирана фактура (`print`)
// го печата и по-късно — тогава и с номера на новата фактура, ако вече е издадена.
// Протоколът е нужен, когато фактурата вече е отразена в регистрите (дневниците)
// на доставчика или получателя (чл. 116, ал. 4); всички екземпляри на
// анулираната фактура се пазят при издателя (ал. 5) — въпросът казва и двете.
//
// Въпросът е `modal.confirm` на App-а, не модал в компонента: прегледът и
// модалът на сметката се затварят веднага след действието, а въпросът трябва
// да остане. Основанието се пише на ръка — облакът не го пази.
//
// `message` подава екранът: в `pages/ordering/` — `useOrderingMessage` (единственият
// вход за toast-ове там), в справките — `App.useApp().message`.

import { useState } from "react";
import { App } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { printInvoiceCancellationPos } from "../api/invoices";
import { ConfirmConsequences } from "../components/ConfirmConsequences";
import { usePrinterNotifications } from "./usePrinterNotifications";
import { blockIfLicenseReadOnly } from "../lib/licenseReadOnly";
import { getApiErrorMessage } from "../utils/translations";

type SuccessMessage = { success: (content: string) => unknown };

export const useInvoiceCancellationProtocol = (message: SuccessMessage) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const { notifyPrinterError } = usePrinterNotifications();
  const [isPrinting, setIsPrinting] = useState(false);

  /** Печатът; грешката на принтера стои, докато не се затвори (`notifyPrinterError`). */
  const print = async (invoiceId: number) => {
    if (blockIfLicenseReadOnly()) return;
    setIsPrinting(true);
    try {
      await printInvoiceCancellationPos(invoiceId);
      message.success(t("invoiceCancellation.printSuccess"));
    } catch (error: unknown) {
      notifyPrinterError(getApiErrorMessage(error, t("invoiceCancellation.printError")));
    } finally {
      setIsPrinting(false);
    }
  };

  /** „Отпечатай протокол?" след анулирането на фактура № `invoiceNumber`. */
  const offer = (invoiceId: number, invoiceNumber: string) => {
    modal.confirm({
      title: t("invoiceCancellation.offerTitle", { number: invoiceNumber }),
      icon: <PrinterOutlined className="!text-pos-accent" />,
      content: (
        <ConfirmConsequences
          lines={[
            { text: t("invoiceCancellation.offerCopies") },
            { text: t("invoiceCancellation.offerReason") },
            { text: t("invoiceCancellation.offerWhen") },
            { text: t("invoiceCancellation.offerOriginal") },
          ]}
          question={t("invoiceCancellation.offerQuestion")}
        />
      ),
      okText: t("invoiceCancellation.print"),
      cancelText: t("invoiceCancellation.notNow"),
      centered: true,
      okButtonProps: { className: "!border-pos-accent !bg-pos-accent" },
      onOk: () => print(invoiceId),
    });
  };

  return { offer, print, isPrinting };
};
